/**
 * OmegaLearningPage — S55 Learning Dashboard (Founder-only).
 *
 * Shows every domain OMEGA is learning, its mastery level (Basic → Beyond
 * Professor), atoms learned, distinct sources, % to next level, and live
 * progress. Reads FROM atoms (GET /omega/learning/domains). The Founder can
 * add a new domain by name ("Add Domain") and pause/resume any domain.
 *
 * Levels are a deterministic learning-depth proxy (atom volume), not an academic
 * credential — labeled as such.
 */

import { useState, type CSSProperties } from 'react';
import { Navigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch } from '@/api/http';

// ── Types ────────────────────────────────────────────────────────────────────

interface LearningDomain {
  id: string;
  domain: string;
  kind: string;
  atoms_learned: number;
  distinct_sources: number;
  level: string;
  level_color: string;
  next_level: string | null;
  pct_to_next: number;
  gaps: string;
  last_learned_at: string | null;
  status: string;
}

interface LearningDomainsResponse {
  domains: LearningDomain[];
  total_atoms: number;
  domain_count: number;
}

// ── Data ─────────────────────────────────────────────────────────────────────

function useLearningDomains() {
  return useQuery({
    queryKey: ['omega', 'learning', 'domains'],
    queryFn: () => apiFetch<LearningDomainsResponse>('/omega/learning/domains'),
    refetchInterval: 12_000,
  });
}

function fmtNum(n: number): string {
  return n.toLocaleString('en-US');
}

function fmtTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

// ── Sub-components ────────────────────────────────────────────────────────────

function DomainCard({
  d,
  onPause,
  onResume,
  busy,
}: {
  d: LearningDomain;
  onPause: (id: string) => void;
  onResume: (id: string) => void;
  busy: boolean;
}) {
  const paused = d.status === 'Stopped';
  const canControl = d.id.length > 0;
  return (
    <div
      style={{
        background: 'var(--omega-surface, rgba(0,0,0,0.02))',
        border: '1px solid var(--omega-rim, rgba(0,0,0,0.1))',
        borderRadius: 8,
        padding: '14px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        opacity: paused ? 0.6 : 1,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span
          style={{
            flexShrink: 0,
            fontSize: '7.5pt',
            fontWeight: 700,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            color: '#1a1a1a',
            background: d.level_color,
            padding: '2px 8px',
            borderRadius: 4,
          }}
        >
          {d.level}
        </span>
        <span
          style={{
            flex: 1,
            fontSize: '10.5pt',
            fontWeight: 600,
            color: 'var(--omega-ink)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={d.domain}
        >
          {d.domain}
        </span>
        {d.kind === 'ingest' && (
          <span style={{ fontSize: '7pt', color: 'var(--omega-ink-subtle)', textTransform: 'uppercase' }}>
            pipe
          </span>
        )}
      </div>

      {/* Progress bar to next level */}
      <div>
        <div
          style={{
            height: 6,
            borderRadius: 3,
            background: 'rgba(0,0,0,0.08)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${Math.max(0, Math.min(100, d.pct_to_next))}%`,
              height: '100%',
              background: d.level_color,
              transition: 'width 0.6s ease',
            }}
          />
        </div>
        <div style={{ fontSize: '7.5pt', color: 'var(--omega-ink-subtle)', marginTop: 3 }}>
          {d.next_level ? `${d.pct_to_next.toFixed(0)}% → ${d.next_level} · ${d.gaps}` : d.gaps}
        </div>
      </div>

      {/* Metrics */}
      <div style={{ display: 'flex', gap: 16, fontSize: '8.5pt', color: 'var(--omega-ink-subtle)' }}>
        <span>
          <strong style={{ color: 'var(--omega-ink)', fontSize: '9.5pt' }}>{fmtNum(d.atoms_learned)}</strong> atoms
        </span>
        <span>
          <strong style={{ color: 'var(--omega-ink)', fontSize: '9.5pt' }}>{fmtNum(d.distinct_sources)}</strong> sources
        </span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: '7.5pt', color: 'var(--omega-ink-subtle)', flex: 1 }}>
          {paused ? 'Paused' : 'Learning'} · {fmtTime(d.last_learned_at)}
        </span>
        {canControl && (
          <button
            disabled={busy}
            onClick={() => (paused ? onResume(d.id) : onPause(d.id))}
            style={{
              fontSize: '7.5pt',
              fontWeight: 600,
              padding: '3px 10px',
              borderRadius: 4,
              border: '1px solid var(--omega-rim, rgba(0,0,0,0.15))',
              background: 'transparent',
              color: paused ? '#16a34a' : 'var(--omega-ink-subtle)',
              cursor: busy ? 'wait' : 'pointer',
            }}
          >
            {paused ? 'Resume' : 'Pause'}
          </button>
        )}
      </div>
    </div>
  );
}

function AddDomain({ onAdd, busy }: { onAdd: (domain: string, parent: string) => void; busy: boolean }) {
  const [domain, setDomain] = useState('');
  const [parent, setParent] = useState('');

  function submit() {
    if (domain.trim()) {
      onAdd(domain.trim(), parent.trim());
      setDomain('');
      setParent('');
    }
  }

  const inputStyle: CSSProperties = {
    border: '1px solid var(--omega-rim, rgba(0,0,0,0.15))',
    borderRadius: 6,
    padding: '7px 10px',
    fontSize: '9.5pt',
    color: 'var(--omega-ink)',
    background: 'var(--omega-surface, #fff)',
    outline: 'none',
  };

  return (
    <div
      style={{
        display: 'flex',
        gap: 8,
        flexWrap: 'wrap',
        alignItems: 'center',
        padding: '12px 14px',
        background: 'var(--omega-surface, rgba(0,0,0,0.02))',
        border: '1px dashed var(--omega-rim, rgba(0,0,0,0.2))',
        borderRadius: 8,
        marginBottom: 20,
      }}
    >
      <input
        value={domain}
        onChange={(e) => setDomain(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && submit()}
        placeholder="Domain to learn (e.g. astronomy)"
        style={{ ...inputStyle, flex: '2 1 200px' }}
      />
      <input
        value={parent}
        onChange={(e) => setParent(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && submit()}
        placeholder="Parent (optional, e.g. astronomy)"
        style={{ ...inputStyle, flex: '1 1 160px' }}
      />
      <button
        disabled={busy || !domain.trim()}
        onClick={submit}
        style={{
          fontSize: '9pt',
          fontWeight: 700,
          padding: '8px 16px',
          borderRadius: 6,
          border: 'none',
          background: domain.trim() ? '#2563eb' : 'rgba(0,0,0,0.15)',
          color: '#fff',
          cursor: busy ? 'wait' : domain.trim() ? 'pointer' : 'not-allowed',
        }}
      >
        {busy ? 'Starting…' : 'Start Learning'}
      </button>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function OmegaLearningPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const qc = useQueryClient();
  const { data, isPending, isError } = useLearningDomains();
  const [busy, setBusy] = useState(false);

  function refresh() {
    void qc.invalidateQueries({ queryKey: ['omega', 'learning', 'domains'] });
  }

  async function addDomain(domain: string, parent: string) {
    setBusy(true);
    try {
      await apiFetch('/omega/learning/domains', {
        method: 'POST',
        body: parent ? { domain, parent } : { domain },
      });
      refresh();
    } finally {
      setBusy(false);
    }
  }

  async function setStatus(id: string, action: 'pause' | 'resume') {
    setBusy(true);
    try {
      await apiFetch(`/omega/learning/domains/${id}/${action}`, { method: 'POST' });
      refresh();
    } finally {
      setBusy(false);
    }
  }

  const domains = data?.domains ?? [];

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-sans)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs crumbs={[{ label: 'OMEGA', to: '/omega' }, { label: 'Learning' }]} />

      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 4, marginTop: 8 }}>
        <h1 style={{ margin: 0, fontSize: '13pt', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
          Learning
        </h1>
        <span style={{ fontSize: '9pt', color: 'var(--omega-ink-subtle)' }}>
          {data ? `${data.domain_count} domains · ${fmtNum(data.total_atoms)} atoms` : ''}
        </span>
      </div>
      <p style={{ fontSize: '8pt', color: 'var(--omega-ink-subtle)', margin: '0 0 16px' }}>
        Level = learning-depth proxy (atom volume × source diversity). Not an academic credential.
      </p>

      <AddDomain onAdd={addDomain} busy={busy} />

      {isPending && <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Loading…</div>}
      {isError && <div style={{ color: '#dc2626', fontSize: '9pt' }}>Failed to load learning progress.</div>}
      {!isPending && !isError && domains.length === 0 && (
        <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt', fontStyle: 'italic' }}>
          No domains learning yet. Add one above, or activate verticals.
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: 14,
        }}
      >
        {domains.map((d) => (
          <DomainCard
            key={`${d.kind}:${d.domain}`}
            d={d}
            onPause={(id) => void setStatus(id, 'pause')}
            onResume={(id) => void setStatus(id, 'resume')}
            busy={busy}
          />
        ))}
      </div>
    </div>
  );
}
